'use client';

import { useKonamiCode } from '@hooks/useKonamiCode';
import { createContext, useContext, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'keep-react';
import { selectIsKonamiUnlocked, setUnlocked } from '@store/features/konamiSlice';

// Kontext für den Entwicklermodus
type DevModeContextType = {
  isDevModeActive: boolean;
  toggleDevMode: () => void;
};

const DevModeContext = createContext<DevModeContextType>({
  isDevModeActive: false,
  toggleDevMode: () => {},
});

// Hook für den Zugriff auf den Entwicklermodus
export const useDevMode = () => useContext(DevModeContext);

// Komponente, die den Entwicklermodus aktiviert
export const DevModeActivator = ({ children }: { children: React.ReactNode }) => {
  // Redux-Status und Dispatch verwenden anstelle von lokalem State
  const isDevModeActive = useSelector(selectIsKonamiUnlocked);
  const dispatch = useDispatch();
  
  // Entwicklermodus umschalten
  const toggleDevMode = useCallback(() => {
    const newStatus = !isDevModeActive;
    dispatch(setUnlocked(newStatus));
    
    // Toast-Benachrichtigung anzeigen
    if (newStatus) {
      toast.success(`🔧 Entwicklermodus aktiviert!`);
    } else {
      toast.info(`🔧 Entwicklermodus deaktiviert!`);
    }
  }, [dispatch, isDevModeActive]);
  
  // Konami-Code-Hook mit Callback für das Umschalten
  useKonamiCode(useCallback(() => {
    // Da der Konami-Code nun den Modus umschaltet, müssen wir die Nachricht
    // anhand des NEUEN Status anzeigen (der inverse des aktuellen Status)
    const willBeActive = !isDevModeActive;
    
    if (willBeActive) {
      toast.success(`🔧 Entwicklermodus aktiviert`);
    } else {
      toast.info(`🔧 Entwicklermodus deaktiviert`);
    }
  }, [isDevModeActive]));
  
  return (
    <DevModeContext.Provider value={{ isDevModeActive, toggleDevMode }}>
      {children}
    </DevModeContext.Provider>
  );
};
